import { motion } from "framer-motion";
import { Camera, Map, Video } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CastleGraphic from "../components/CastleGraphic";

/**
 * Scene 50 — The Castle = Core Services (photos, floorplans, video).
 * Castle silhouette appears, central keep glows.
 */

const services = [
  { icon: Camera, label: "Photos" },
  { icon: Map, label: "Floorplans" },
  { icon: Video, label: "Video" },
];

export default function Scene50_TheCastle() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-the-castle">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 50% 60%, rgba(212,148,10,0.14) 0%, transparent 60%)",
            "radial-gradient(ellipse at 50% 60%, rgba(233,168,32,0.22) 0%, transparent 60%)",
            "radial-gradient(ellipse at 50% 60%, rgba(212,148,10,0.14) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative w-full h-full flex flex-col">
        {/* The castle */}
        <div className="flex-1 mt-2 mx-auto w-full max-w-[1400px] relative">
          <CastleGraphic layer={1} />

          {/* Floating callout — anchored top-left so the castle silhouette stays clear */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10"
            data-testid="castle-callout"
          >
            <div
              className="rounded-2xl px-6 py-3 glass"
              style={{
                background: "rgba(233,168,32,0.10)",
                border: "1px solid rgba(233,168,32,0.50)",
                boxShadow: "0 0 60px rgba(233,168,32,0.25)",
              }}
            >
              <div
                className="text-[10px] tracking-[0.32em] uppercase mb-1"
                style={{ color: "#e9a820" }}
              >
                The Castle
              </div>
              <div
                className="kn-display text-xl sm:text-2xl text-white"
                style={{ fontWeight: 700 }}
              >
                Core services
              </div>
            </div>
          </motion.div>
        </div>

        {/* Service tags */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 2.0 }}
          className="flex items-center justify-center gap-3 sm:gap-5 mt-4 mb-2"
        >
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 2.2 + i * 0.18,
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="flex items-center gap-2.5 rounded-full px-5 py-2.5 glass-dark"
                data-testid={`castle-service-${i}`}
              >
                <Icon className="w-4 h-4" style={{ color: "#e9a820" }} />
                <span
                  className="text-sm sm:text-base"
                  style={{ color: "rgba(255,255,255,0.92)", fontWeight: 600 }}
                >
                  {s.label}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </SceneShell>
  );
}
